/**
 * The muscles the app counts weekly sets for.
 *
 * This is the lifter's vocabulary, not the anatomy atlas: sixteen groups a
 * person programs for, plus neck. The finer region ids in engine/taxonomy.ts
 * stay available for search and descriptions; no study supports counting sets
 * per muscle head, so nothing here does.
 */

export const MUSCLE_IDS = [
  'chest',
  'lats',
  'upper_back',
  'front_delts',
  'side_delts',
  'rear_delts',
  'biceps',
  'triceps',
  'forearms',
  'quads',
  'hamstrings',
  'glutes',
  'adductors',
  'calves',
  'abs',
  'lower_back',
  'neck',
] as const
export type MuscleId = (typeof MUSCLE_IDS)[number]

export type MuscleArea = 'push' | 'pull' | 'arms' | 'legs' | 'core'

export interface MuscleInfo {
  id: MuscleId
  name: string
  short: string
  area: MuscleArea
  /** One plain sentence: what it is and what trains it. */
  about: string
}

export const MUSCLES: Readonly<Record<MuscleId, MuscleInfo>> = {
  chest: { id: 'chest', name: 'Chest', short: 'Chest', area: 'push', about: 'Pectorals. Trained directly by presses, push-ups, dips and flyes.' },
  lats: { id: 'lats', name: 'Lats', short: 'Lats', area: 'pull', about: 'The wide back muscles under your arms. Trained by pulldowns, pull-ups, rows and pullovers.' },
  upper_back: { id: 'upper_back', name: 'Upper back & traps', short: 'Upper back', area: 'pull', about: 'Trapezius and rhomboids between the shoulder blades. Shrugs and wide rows train them directly; most pulling trains them indirectly.' },
  front_delts: { id: 'front_delts', name: 'Front delts', short: 'Front delts', area: 'push', about: 'Front of the shoulder. Overhead presses and front raises train them directly; every chest press trains them indirectly.' },
  side_delts: { id: 'side_delts', name: 'Side delts', short: 'Side delts', area: 'push', about: 'Side of the shoulder, which makes shoulders look wider. Lateral raises and upright rows train them directly.' },
  rear_delts: { id: 'rear_delts', name: 'Rear delts', short: 'Rear delts', area: 'pull', about: 'Back of the shoulder. Rear-delt flyes and face pulls train them directly; rows train them indirectly.' },
  biceps: { id: 'biceps', name: 'Biceps', short: 'Biceps', area: 'arms', about: 'The elbow flexors: biceps, brachialis and brachioradialis. Curls train them directly; rows and pulldowns train them indirectly.' },
  triceps: { id: 'triceps', name: 'Triceps', short: 'Triceps', area: 'arms', about: 'Back of the upper arm. Extensions and close-grip presses train them directly; chest and shoulder presses train them indirectly.' },
  forearms: { id: 'forearms', name: 'Forearms', short: 'Forearms', area: 'arms', about: 'Wrist flexors and extensors. Wrist curls, reverse curls and heavy holds train them. Ordinary gripping during rows is not counted.' },
  quads: { id: 'quads', name: 'Quads', short: 'Quads', area: 'legs', about: 'Front of the thigh. Squats, leg presses, lunges and leg extensions train them directly.' },
  hamstrings: { id: 'hamstrings', name: 'Hamstrings', short: 'Hamstrings', area: 'legs', about: 'Back of the thigh. Leg curls and Romanian deadlifts train them directly. Squats barely grow them.' },
  glutes: { id: 'glutes', name: 'Glutes', short: 'Glutes', area: 'legs', about: 'Hip muscles. Squats, lunges, hip thrusts, hinges and abduction train them.' },
  adductors: { id: 'adductors', name: 'Adductors', short: 'Adductors', area: 'legs', about: 'Inner thigh. Deep squats train them indirectly; the adduction machine trains them directly.' },
  calves: { id: 'calves', name: 'Calves', short: 'Calves', area: 'legs', about: 'Lower leg. Standing calf raises train all of the calf; seated raises mostly the deeper soleus.' },
  abs: { id: 'abs', name: 'Abs & obliques', short: 'Abs', area: 'core', about: 'Front and sides of the trunk. Crunches, leg raises and rollouts train them. Bracing during squats is not counted.' },
  lower_back: { id: 'lower_back', name: 'Lower back', short: 'Lower back', area: 'core', about: 'The spinal erectors. Back extensions train them directly; deadlifts and Romanian deadlifts indirectly.' },
  neck: { id: 'neck', name: 'Neck', short: 'Neck', area: 'core', about: 'Neck flexors and extensors. Only counted when you train the neck on purpose.' },
}

export const AREA_LABEL: Readonly<Record<MuscleArea, string>> = {
  push: 'Push', pull: 'Pull', arms: 'Arms', legs: 'Legs', core: 'Core',
}

/**
 * How much weekly work a lifter wants for a muscle. The numbers are starting
 * points expressed in counted sets (direct 1, indirect 0.5), chosen to sit
 * inside the efficiency tiers of Pelland et al. 2026. They are not personal
 * prescriptions and the app never stops you going past them.
 */
export type Emphasis = 'focus' | 'grow' | 'maintain' | 'off'

export const EMPHASIS_TARGET: Readonly<Record<Emphasis, { low: number; high: number } | null>> = {
  focus: { low: 14, high: 22 },
  grow: { low: 10, high: 16 },
  maintain: { low: 4, high: 8 },
  off: null,
}

export const EMPHASIS_LABEL: Readonly<Record<Emphasis, string>> = {
  focus: 'Focus', grow: 'Grow', maintain: 'Maintain', off: 'Off',
}

export const DEFAULT_EMPHASIS: Readonly<Record<MuscleId, Emphasis>> = {
  chest: 'grow',
  lats: 'grow',
  upper_back: 'grow',
  front_delts: 'maintain',
  side_delts: 'grow',
  rear_delts: 'grow',
  biceps: 'grow',
  triceps: 'grow',
  forearms: 'off',
  quads: 'grow',
  hamstrings: 'grow',
  glutes: 'grow',
  adductors: 'maintain',
  calves: 'grow',
  abs: 'maintain',
  lower_back: 'off',
  neck: 'off',
}

export function isMuscleId(value: unknown): value is MuscleId {
  return typeof value === 'string' && (MUSCLE_IDS as readonly string[]).includes(value)
}
